import { NotFoundPage } from '@elotech/components';
import AuthService from 'common/auth/AuthService.js';
import { lazy } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import * as keycloakRoles from '../permissions/keycloakRoles.js';
import * as standaloneSeletores from '../permissions/seletores.js';
import ConditionalRoute from './ConditionalRoute.js';
import * as crudTypes from './CrudTypes.js';
import { ProtectedRoute } from './ProtectedRoute.js';

const PessoaView = lazy(() => import('../pessoa/PessoaView.js'));
const PessoaForm = lazy(() => import('../pessoa/PessoaForm.js'));
const DashboardBuilder = lazy(() =>
  import('../dashboards/DashboardBuilder.js')
);
const DashboardList = lazy(() => import('../dashboards/DashboardList.js'));
const PessoasList = lazy(() => import('../pessoa/PessoasList.js'));
const AtualizacaoPessoa = lazy(() => import('../pessoa/AtualizacaoPessoa.js'));
const RoteiroForm = lazy(() => import('../roteiros/RoteiroForm.js'));
const RoteiroList = lazy(() => import('../roteiros/RoteiroList.js'));
const SecretariaForm = lazy(() => import('../secretarias/SecretariaForm.js'));
const SecretariaList = lazy(() => import('../secretarias/SecretariaList.js'));
const SituacaoForm = lazy(() => import('../situacoes/SituacaoForm.js'));
const SituacaoList = lazy(() => import('../situacoes/SituacaoList.js'));
const TipoNotificacaoForm = lazy(() =>
  import('../tiposnotificacao/TipoNotificacaoForm.js')
);
const TipoNotificacaoList = lazy(() =>
  import('../tiposnotificacao/TipoNotificacaoList.js')
);
const TipoParticipanteForm = lazy(() =>
  import('../tiposparticipante/TipoParticipanteForm.js')
);
const AvaliacaoProcessosList = lazy(() =>
  import('../avaliacaoProcesso/AvaliacaoProcessosList.js')
);
const ProcessosNovoList = lazy(() =>
  import('../processos-novo/ProcessosNovoList.js')
);
const ProcessosAgrupados = lazy(() =>
  import('../processos-agrupados/ProcessosAgrupados.js')
);
const AssinaturaConfigForm = lazy(() =>
  import('../processos-novo/assinatura/AssinaturaConfigForm.js')
);
const ArquivosAssinaturaList = lazy(() =>
  import('../processos-novo/assinatura/AssinaturaList.js')
);
const ProcessoNovoForm = lazy(() =>
  import('../processos-novo/ProcessoNovoForm.js')
);
const ProcessoNovoView = lazy(() =>
  import('../processos-novo/ProcessoNovoView.js')
);
const ProcessoNovoHistory = lazy(() =>
  import('../processos-novo/ProcessoNovoHistory.js')
);
const TipoParticipanteList = lazy(() =>
  import('../tiposparticipante/TipoParticipanteList.js')
);
const TipoProcessoForm = lazy(() =>
  import('../tiposprocesso/TipoProcessoForm.js')
);
const TipoProcessoList = lazy(() =>
  import('../tiposprocesso/TipoProcessoList.js')
);
const UnidadeForm = lazy(() => import('../unidades/UnidadeForm.js'));
const UnidadeList = lazy(() => import('../unidades/UnidadeList.js'));
const UnidadeOrcamentariaForm = lazy(() =>
  import('../unidadesOrcamentaria/UnidadeOrcamentariaForm.js')
);
const UnidadeOrcamentariaList = lazy(() =>
  import('../unidadesOrcamentaria/UnidadeOrcamentariaList.js')
);
const CompletarCadastroKeycloakForm = lazy(() =>
  import('../usuarios/CompletarCadastroKeycloakForm.js')
);
const UsuarioForm = lazy(() => import('../usuarios/UsuarioForm.js'));
const UsuarioList = lazy(() => import('../usuarios/UsuarioList.js'));
const AssuntoHistory = lazy(() => import('../assuntos/AssuntoHistory.js'));
const TipoProcessoHistory = lazy(() =>
  import('../tiposprocesso/TipoProcessoHistory.js')
);
const PessoaHistory = lazy(() => import('../pessoa/PessoaHistory.js'));
const AberturaProcessoExterno = lazy(() =>
  import('aberturaProcessoExterno/AberturaProcessoExterno.js')
);
const ConsultaAutenticidadeForm = lazy(() =>
  import('../processos-novo/assinatura/ConsultaAutenticidadeForm.js')
);
const VincularProcessos = lazy(() =>
  import('../vincular-processos/VincularProcessos.js')
);
const AssuntoForm = lazy(() => import('../assuntos/AssuntoForm.js'));
const AssuntosList = lazy(() => import('../assuntos/AssuntosList.js'));
const LoginContainer = lazy(() => import('../auth/LoginContainer.react.js'));
const BairroForm = lazy(() => import('../bairros/BairroForm.js'));
const BairroList = lazy(() => import('../bairros/BairroList.js'));
const CaixaForm = lazy(() => import('../caixas/CaixaForm.js'));
const CaixaList = lazy(() => import('../caixas/CaixaList.js'));
const CidadeForm = lazy(() => import('../cidades/CidadeForm.js'));
const CidadeList = lazy(() => import('../cidades/CidadeList.js'));
const Logout = lazy(() => import('common/logout/Logout.react.js'));
const ComprovanteEntregaProcessoForm = lazy(() =>
  import('../comprovanteEntregaProcesso/ComprovanteEntregaProcessoForm.js')
);
const ComprovanteEntregaProcessoList = lazy(() =>
  import('../comprovanteEntregaProcesso/ComprovanteEntregaProcessoList.js')
);
const ConsultaProcessos = lazy(() =>
  import('../consultaProcesso/ConsultaProcessos.js')
);
const CoordenadoriaForm = lazy(() =>
  import('../coordenadorias/CoordenadoriaForm.js')
);
const CoordenadoriaList = lazy(() =>
  import('../coordenadorias/CoordenadoriaList.js')
);
const CorrecaoFluxo = lazy(() => import('../correcaoFluxo/CorrecaoFluxo.js'));
const DiretoriaForm = lazy(() => import('../diretorias/DiretoriaForm.js'));
const DiretoriaList = lazy(() => import('../diretorias/DiretoriaList.js'));
const DocumentoForm = lazy(() => import('../documentos/DocumentoForm.js'));
const DocumentoList = lazy(() => import('../documentos/DocumentoList.js'));
const EnviarProcessosTributos = lazy(() =>
  import('../enviar-processos-fila/EnviarProcessosTributos.js')
);
const EstatisticasProcesso = lazy(() =>
  import('../relatorios/EstatisticasProcesso.js')
);
const GerenciaForm = lazy(() => import('../gerencias/GerenciaForm.js'));
const GerenciaList = lazy(() => import('../gerencias/GerenciaList.js'));
const GrauSigiloForm = lazy(() => import('../grauSigilos/GrauSigiloForm.js'));
const GrauSigiloList = lazy(() => import('../grauSigilos/GrauSigiloList.js'));
const Home = lazy(() => import('../home/Home.js'));
const LocalForm = lazy(() => import('../locais/LocalForm.js'));
const LocalList = lazy(() => import('../locais/LocalList.js'));
const LogradouroForm = lazy(() => import('../logradouros/LogradouroForm.js'));
const LogradouroList = lazy(() => import('../logradouros/LogradouroList.js'));
const RelatorioComprovanteEncaminhamento = lazy(() =>
  import('../relatorios/ComprovanteEncaminhamento.js')
);
const RelatorioPocessoGeral = lazy(() => import('../relatorios/Processo.js'));
const RelatorioTramite = lazy(() => import('../relatorios/Tramite.js'));
const RelatorioTramitePorLocal = lazy(() =>
  import('../relatorios/TramitePorLocal.js')
);
const RelatorioEtiqueta = lazy(() => import('../relatorios/Etiqueta.js'));
const PageParametros = lazy(() => import('../parametros/ParametroList.js'));
const ModeloImpressaoForm = lazy(() =>
  import('../modelosImpressao/ModeloImpressaoForm.js')
);
const ModeloImpressaoList = lazy(() =>
  import('../modelosImpressao/ModeloImpressaoList.js')
);
const UnicoConsultaDinamica = lazy(() =>
  import('../iframe-components/UnicoConsultaDinamica')
);
const UnicoRelatorioDinamico = lazy(() =>
  import('../iframe-components/UnicoRelatorioDinamico')
);
const ModelosDocumentosList = lazy(() =>
  import('../modelosDocumentos/modelos/ModelosDocumentosList')
);
const ModelosDocumentosForm = lazy(() =>
  import('../modelosDocumentos/modelos/ModelosDocumentosForm')
);
const VariaveisList = lazy(() =>
  import('../modelosDocumentos/variaveis/VariaveisList')
);
const VariaveisForm = lazy(() =>
  import('../modelosDocumentos/variaveis/VariaveisForm')
);
const UnicoCadastrosProtocolo = lazy(() =>
  import('../iframe-components/UnicoCadastrosProtocolo')
);
const ProcessosLocais = lazy(() => import('../indicadores/ProcessosLocais.js'));

const routerParamsFactory = (config, state) => {
  let userAccess = null;

  if (config.isCloud) {
    userAccess = state.keycloak.get('userAccess')
      ? state.keycloak.getIn(['userAccess', 'roles']).toJS()
      : {};
  } else {
    userAccess = state.auth.get('userAccess')
      ? state.auth.getIn(['userAccess', 'telas']).toJS()
      : {};
  }

  return {
    config,
    userAccess
  };
};

export default function Routes({ config = {}, state, isIFrame }) {
  const location = useLocation();
  const routerParams = routerParamsFactory(config, state);

  const getPath = (pathName, idEntidade) => {
    return idEntidade ? `/:id${pathName}` : pathName;
  };
  const getPathWithEntidade = (pathName, idEntidade) => {
    return idEntidade ? `/${idEntidade}${pathName}` : pathName;
  };
  const getConfig = () => config;

  const renderHome = () => {
    // apenas o keycloak possui a permissão para a home/dashboards

    const isLogged = AuthService.isLogged();

    if (isLogged && config.isCloud) {
      return ProtectedRoute(routerParams, null, keycloakRoles.home, '/', Home);
    }

    return (
      <ConditionalRoute
        exact
        path={'/'}
        redirectPath={getPathWithEntidade(
          '/consultaProcesso',
          config.idEntidade
        )}
        condition={isLogged}
        component={Home}
      />
    );
  };

  const routeAberturaProcessoExterno = idEntidade => {
    return (
      <ConditionalRoute
        exact
        path={getPath('/aberturaProcessoExterno', idEntidade)}
        childProps={{ getConfig, isIFrame }}
        condition={config.usaProcessoExterno}
        component={AberturaProcessoExterno}
      />
    );
  };

  const routeOuvidoria = idEntidade => {
    return (
      <ConditionalRoute
        exact
        path={getPath('/ouvidoria', idEntidade)}
        condition={config.usaOuvidoria}
        childProps={{ getConfig, isIFrame, isOuvidoria: true }}
        component={AberturaProcessoExterno}
      />
    );
  };

  const routeConsultaAutenticidade = idEntidade => {
    return (
      <ConditionalRoute
        exact
        path={getPath('/consulta-autenticidade', idEntidade)}
        condition={true}
        component={ConsultaAutenticidadeForm}
      />
    );
  };

  const routeConsultaProcesso = idEntidade => {
    return (
      <Route
        exact
        path={getPath('/consultaProcesso', idEntidade)}
        render={props => <ConsultaProcessos {...props} />}
      />
    );
  };

  return (
    <Switch>
      {renderHome()}
      {location.pathname === '/home' && <Redirect to="/" />}
      <ConditionalRoute
        exact
        path="/login"
        condition={config.isStandalone}
        component={LoginContainer}
      />
      <ConditionalRoute
        exact
        path="/usuario/me"
        condition={config.isCloud}
        component={CompletarCadastroKeycloakForm}
      />
      {routeAberturaProcessoExterno()}
      {routeOuvidoria()}
      {routeConsultaAutenticidade()}
      {routeConsultaProcesso()}
      {config.idEntidade && routeAberturaProcessoExterno(config.idEntidade)}
      {config.idEntidade && routeOuvidoria(config.idEntidade)}
      {config.idEntidade && routeConsultaAutenticidade(config.idEntidade)}
      {config.idEntidade && routeConsultaProcesso(config.idEntidade)}
      <Route exact path="/logout" component={Logout} />
      {/**  Secured Routes **/}
      {/* Dashboards CubeJs */}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.cubejsDashboards,
        keycloakRoles.cubeJs,
        '/dashboards',
        DashboardList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.cubejsDashboards,
        keycloakRoles.cubeJsUpdate,
        '/dashboards/:id',
        DashboardBuilder,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {/** Cadastros **/}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.atualizacaoPessoa,
        keycloakRoles.atualizacaoPessoa,
        '/atualizacao/pessoas',
        AtualizacaoPessoa,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.assunto,
        keycloakRoles.assunto,
        '/assuntos',
        AssuntosList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.assunto,
        keycloakRoles.assuntoUpdate,
        '/assunto/:id',
        AssuntoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.assunto,
        keycloakRoles.assunto,
        '/assunto/:id/history',
        AssuntoHistory,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.bairro,
        keycloakRoles.bairro,
        '/bairros',
        BairroList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.bairro,
        keycloakRoles.bairroCreate,
        '/bairro/:id',
        BairroForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.caixa,
        keycloakRoles.caixa,
        '/caixas',
        CaixaList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.caixa,
        keycloakRoles.caixaUpdate,
        '/caixa',
        CaixaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.cidade,
        keycloakRoles.cidade,
        '/cidades',
        CidadeList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.cidade,
        keycloakRoles.cidadeUpdate,
        '/cidade/:id',
        CidadeForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.coordenadoria,
        keycloakRoles.coordenadoria,
        '/coordenadorias',
        CoordenadoriaList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.coordenadoria,
        keycloakRoles.coordenadoriaUpdate,
        '/coordenadoria/:id',
        CoordenadoriaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.diretoria,
        keycloakRoles.diretoria,
        '/diretorias',
        DiretoriaList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.diretoria,
        keycloakRoles.diretoriaUpdate,
        '/diretoria/:id',
        DiretoriaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.documentos,
        keycloakRoles.documentos,
        '/documentos',
        DocumentoList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.documentos,
        keycloakRoles.documentosUpdate,
        '/documento/:id',
        DocumentoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.gerencia,
        keycloakRoles.gerencia,
        '/gerencias',
        GerenciaList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.gerencia,
        keycloakRoles.gerenciaUpdate,
        '/gerencia/:id',
        GerenciaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.grauSigilo,
        keycloakRoles.grauSigilo,
        '/grauSigilos',
        GrauSigiloList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.grauSigilo,
        keycloakRoles.grauSigiloUpdate,
        '/grauSigilo/:id',
        GrauSigiloForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.local,
        keycloakRoles.local,
        '/locais',
        LocalList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.local,
        keycloakRoles.localUpdate,
        '/local/:id',
        LocalForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.modeloImpressao,
        keycloakRoles.modeloImpressao,
        '/modelos-impressao',
        ModeloImpressaoList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.modeloImpressao,
        keycloakRoles.modeloImpressaoUpdate,
        '/modelo-impressao/:id',
        ModeloImpressaoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.logradouro,
        keycloakRoles.logradouro,
        '/logradouros',
        LogradouroList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.logradouro,
        keycloakRoles.logradouroUpdate,
        '/logradouro/:id',
        LogradouroForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.pessoa,
        keycloakRoles.pessoa,
        '/pessoas',
        PessoasList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.pessoa,
        keycloakRoles.pessoaUpdate,
        '/pessoa/:id',
        PessoaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.pessoa,
        keycloakRoles.pessoa,
        '/pessoa/view/:id',
        PessoaView,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.roteiro,
        keycloakRoles.roteiro,
        '/roteiros',
        RoteiroList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.roteiro,
        keycloakRoles.roteiroUpdate,
        '/roteiro/:id',
        RoteiroForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.secretaria,
        keycloakRoles.secretaria,
        '/secretarias',
        SecretariaList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.secretaria,
        keycloakRoles.secretariaUpdate,
        '/secretaria/:id',
        SecretariaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.situacao,
        keycloakRoles.situacao,
        '/situacoes',
        SituacaoList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.situacao,
        keycloakRoles.situacaoUpdate,
        '/situacao/:id',
        SituacaoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipo,
        keycloakRoles.tipo,
        '/tiposprocesso',
        TipoProcessoList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipo,
        keycloakRoles.tipoUpdate,
        '/tipoprocesso/:id',
        TipoProcessoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipoNotificacao,
        keycloakRoles.tipoNotificacao,
        '/tiposnotificacao',
        TipoNotificacaoList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipoNotificacao,
        keycloakRoles.tipoNotificacaoUpdate,
        '/tiponotificacao/:id',
        TipoNotificacaoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipoParticipante,
        keycloakRoles.tipoParticipante,
        '/tipos-participante',
        TipoParticipanteList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipoParticipante,
        keycloakRoles.tipoParticipanteUpdate,
        '/tipo-participante/:id',
        TipoParticipanteForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unidade,
        keycloakRoles.unidade,
        '/unidades',
        UnidadeList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unidade,
        keycloakRoles.unidadeUpdate,
        '/unidade/:id',
        UnidadeForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unidadeOrcamentaria,
        keycloakRoles.unidadeOrcamentaria,
        '/unidadesOrcamentaria',
        UnidadeOrcamentariaList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unidadeOrcamentaria,
        keycloakRoles.unidadeOrcamentariaCreate,
        '/unidadeOrcamentaria',
        UnidadeOrcamentariaForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.usuario,
        keycloakRoles.usuario,
        '/usuarios',
        UsuarioList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.usuario,
        keycloakRoles.usuarioUpdate,
        '/usuario/:id',
        UsuarioForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {/** Processos **/}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.processos,
        keycloakRoles.processos,
        '/processos-avaliacao',
        AvaliacaoProcessosList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.processos,
        keycloakRoles.processos,
        '/processos-novo',
        ProcessosNovoList,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.processos,
        keycloakRoles.processos,
        '/tramitacao-dinamica',
        ProcessosAgrupados,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.assinatura,
        keycloakRoles.assinatura,
        '/arquivos-assinatura',
        ArquivosAssinaturaList,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.assinatura,
        keycloakRoles.assinatura,
        '/assinatura-configuracao',
        AssinaturaConfigForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.processos,
        keycloakRoles.processos,
        '/processos-novo/view/',
        ProcessoNovoView,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.processos,
        keycloakRoles.processos,
        '/processo-novo/',
        ProcessoNovoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.processos,
        keycloakRoles.processos,
        '/processos-novo/history/',
        ProcessoNovoHistory,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.anexar,
        keycloakRoles.anexar,
        '/vincular-processos',
        VincularProcessos,
        crudTypes.CRUD_TYPE_NEW
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.comprovante,
        keycloakRoles.comprovante,
        '/comprovantes-entregas',
        ComprovanteEntregaProcessoList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.comprovante,
        keycloakRoles.comprovanteUpdate,
        '/comprovante-entrega/:id',
        ComprovanteEntregaProcessoForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.correcaoFluxo,
        keycloakRoles.correcaoFluxo,
        '/correcaoFluxo',
        CorrecaoFluxo,
        crudTypes.CRUD_TYPE_NEW
      )}
      {/* Indicadores */}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.indicadorProcessosLocais,
        keycloakRoles.indicadorProcessosLocais,
        '/indicador-processos-locais',
        ProcessosLocais,
        crudTypes.CRUD_TYPE_READ
      )}
      {/* Relatórios */}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.relatorios,
        keycloakRoles.relatorioGeral,
        '/processos/relatorios/geral',
        RelatorioPocessoGeral,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.relatorios,
        keycloakRoles.relatorioComprovanteEncaminhamento,
        '/processos/relatorios/comprovante-encaminhamento',
        RelatorioComprovanteEncaminhamento,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.relatorios,
        keycloakRoles.relatorioTramite,
        '/processos/relatorios/tramite',
        RelatorioTramite,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.relatorios,
        keycloakRoles.relatorioEtiqueta,
        '/processos/relatorios/etiqueta',
        RelatorioEtiqueta,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.relatorios,
        keycloakRoles.relatorioEstatisticasProcessos,
        '/processos/relatorios/estatisticas-processos',
        EstatisticasProcesso,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.relatorios,
        keycloakRoles.relatorioTramite,
        '/processos/relatorios/tramite-por-local',
        RelatorioTramitePorLocal,
        crudTypes.CRUD_TYPE_READ
      )}
      {/* Parametros */}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.parametros,
        keycloakRoles.parametros,
        '/parametros',
        PageParametros,
        crudTypes.CRUD_TYPE_READ
      )}
      {/* Fim Parametros */}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.integracaoTributos,
        keycloakRoles.integracaoTributos,
        '/enviar-processos',
        EnviarProcessosTributos,
        crudTypes.CRUD_TYPE_NEW
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.pessoa,
        keycloakRoles.pessoa,
        '/pessoa/:id/history',
        PessoaHistory,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.tipo,
        keycloakRoles.tipo,
        '/tipoprocesso/:id/history',
        TipoProcessoHistory,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoConsultaDinamica,
        keycloakRoles.unicoConsultaDinamica,
        '/unico-consultas-dinamicas',
        UnicoConsultaDinamica,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoRelatorioDinamico,
        keycloakRoles.unicoRelatorioDinamico,
        '/unico-relatorios-dinamicos',
        UnicoRelatorioDinamico,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.modelosDocumentos,
        keycloakRoles.modelosDocumentos,
        '/modelos-documentos',
        ModelosDocumentosList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.modelosDocumentos,
        keycloakRoles.modelosDocumentosUpdate,
        '/modelos-documentos/:id',
        ModelosDocumentosForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {/* Cadastros Único */}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoCadastros,
        keycloakRoles.unicoCadastros,
        '/unico-agencia',
        UnicoCadastrosProtocolo,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoCadastros,
        keycloakRoles.unicoCadastros,
        '/unico-bairro',
        UnicoCadastrosProtocolo,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoCadastros,
        keycloakRoles.unicoCadastros,
        '/unico-cidade',
        UnicoCadastrosProtocolo,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoCadastros,
        keycloakRoles.unicoCadastros,
        '/unico-legislacao',
        UnicoCadastrosProtocolo,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoCadastros,
        keycloakRoles.unicoCadastros,
        '/unico-logradouro',
        UnicoCadastrosProtocolo,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.unicoCadastros,
        keycloakRoles.unicoCadastros,
        '/unico-pessoa',
        UnicoCadastrosProtocolo,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.modelosDocumentos,
        keycloakRoles.variaveis,
        '/variaveis',
        VariaveisList,
        crudTypes.CRUD_TYPE_READ
      )}
      {ProtectedRoute(
        routerParams,
        standaloneSeletores.modelosDocumentos,
        keycloakRoles.variaveisUpdate,
        '/variaveis/:id',
        VariaveisForm,
        crudTypes.CRUD_TYPE_UPDATE
      )}
      <Route path="*" component={NotFoundPage} />
    </Switch>
  );
}
